import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { API_BASE_URL } from "@/config/api";
import { isOwnerRole } from "@/utils/roles";
import "./style.css";

export default function NetExposureDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exposureData, setExposureData] = useState([]);
  const [meta, setMeta] = useState({ totalEvents: 0, totalExposure: 0 });

  // Helper to get auth token
  const getAuthToken = () => {
    const activeRole = localStorage.getItem('activeRole');
    if (isOwnerRole(activeRole)) {
      return localStorage.getItem('ownerToken');
    } else if (activeRole === 'staff') {
      return localStorage.getItem('staffToken');
    }
    return localStorage.getItem('token');
  };

  // Fetch net exposure data from API
  const fetchNetExposure = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_BASE_URL}/lords/net-exposure`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }
      );

      if (response.data && response.data.success) {
        // Transform API response to match component structure
        const transformedData = response.data.data.map((event, index) => ({
          id: event.id || index + 1,
          eventId: event.eventId,
          eventName: event.eventName,
          eventDate: event.eventDate ? new Date(event.eventDate).toLocaleString() : '',
          category: event.category || 'match_odds',
          markets: event.markets.map((market, mIndex) => ({
            id: market.id || mIndex + 1,
            marketName: market.marketName,
            matchId: market.matchId,
            selections: market.selections || {},
            stake: market.stake || 0,
            pl: market.pl || 0,
          })),
        }));

        setExposureData(transformedData);
        setMeta(response.data.meta || { totalEvents: 0, totalExposure: 0 });
      } else {
        setExposureData([]);
      }
    } catch (error) {
      console.error("Error fetching net exposure data:", error);
      setExposureData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchNetExposure();
  }, []);

  const handleRefresh = async () => {
    await fetchNetExposure();
  };

  return (

      <div className="net-exposure w-full mx-auto bg-[#0f1224] h-auto">
        {/* Header Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-2"
        >
           <div className="flex justify-between items-start">
            <div className="w-full mb-1">
              <div
                style={{ color: "#cae0e8", fontSize: "24px", }}
                className="font-semibold "
              >
                Net Exposure
              </div>
              <div className="w-full h-px bg-white"></div>
            </div>
          </div>
       
        </div>
     

        {/* Table Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", }}
          className="shadow-sm p-4 "
        >
          <div style={{ marginTop: '-15px' }} className="overflow-x-auto">
            {/* Column Header Table */}
            <table className="table m-b-5 w-full text-sm">
              <thead>
                <tr>
                  <th style={{ minWidth: "10%", maxWidth: "10%", width: "10%" }}></th>
                  <th style={{ minWidth: "8%", maxWidth: "8%", width: "8%" }}></th>
                  <th className="text-center" style={{ minWidth: "8%", maxWidth: "8%", width: "8%", color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                    Stake
                  </th>
                  <th className="text-center" style={{ minWidth: "8%", maxWidth: "8%", width: "8%", color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                    1
                  </th>
                  <th className="text-center" style={{ minWidth: "8%", maxWidth: "8%", width: "8%", color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                    X
                  </th>
                  <th className="text-center" style={{ minWidth: "8%", maxWidth: "8%", width: "8%", color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                    2
                  </th>
                  <th style={{ minWidth: "18%", maxWidth: "18%", width: "18%" }}></th>
                  <th style={{ minWidth: "16%", maxWidth: "16%", width: "16%" }}></th>
                  <th className="text-center" style={{ minWidth: "8%", maxWidth: "8%", width: "8%", color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                    Stake
                  </th>
                  <th className="text-center" style={{ minWidth: "8%", maxWidth: "8%", width: "8%", color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                    <div style={{ marginRight: '-30px' }} className="flex flex-col gap-2 ">
                      <div className="flex items-center gap-2">
                        <div style={{ fontSize: '12px', color: '#fff', fontWeight: 'bold', marginRight: '10px' }}>{meta.totalEvents}</div>
                        <button
                          onClick={handleRefresh}
                          disabled={loading}
                          className="bg-[#11374a] hover:bg-[#1a4f66] text-white text-[14px] font-medium px-2 py-1 transition-colors disabled:opacity-50"
                        >
                          {loading ? "Refreshing..." : "Refresh"}
                        </button>
                      </div>
                      <div>P/L</div>
                    </div>
                  </th>
                </tr>
              </thead>
            </table>
            
            {loading ? (
              <div className="text-center py-8 text-white">Loading...</div>
            ) : exposureData.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No exposure data found</div>
            ) : (
              exposureData.map((event, eventIndex) => (
                <table key={event.id} className="table m-b-5 w-full text-sm">
                  <thead>
                    <tr className="title">
                      <td colSpan="11" className="text-white px-4 py-2">
                        {event.eventName}
                        <span className="m-l-10">({event.eventDate})</span>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Category Row */}
                    <tr className="fancy-tile">
                      <td style={{fontSize: '12px'}} colSpan="6" className="left-empty px-4 py-2 text-white font-bold">
                        {event.eventName}
                      </td>
                      <td colSpan="5" className="left-empty text-right px-4 py-2 text-white font-bold">
                        {event.category}
                      </td>
                    </tr>
                    {/* Market Rows */}
                    {event.markets.map((market, marketIndex) => (
                      <tr key={market.id}>
                        <td style={{ background: 'transparent' }} colSpan="6" className="left-empty"></td>
                        <td className="cell-market-title  py-2 text-white flex justify-between">
                          <span>{market.marketName}</span>
                          <span className="">
                            <ul className="icon-group" style={{ display: 'inline-flex', justifyContent: 'space-between', listStyle: 'none', padding: 0, margin: 0, marginLeft: '10px', verticalAlign: 'middle' }}>
                              <li className="cell-market-workstation">
                                <a
                                  href={`/gamedetailnew/4/${event.eventId}`}
                                  className="game-status"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/game-details/4/${event.eventId}/1`);
                                  }}
                                  style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                >
                                  <img
                                    src="https://d3kb8xz339pq18.cloudfront.net/v12/static/backend/images/icons/exposure.png"
                                    className="icon"
                                    alt="exposure"
                                    style={{ width: '20px', height: '20px', verticalAlign: 'middle' }}
                                  />
                                </a>
                              </li>
                            </ul>
                          </span>
                        </td>
                        <td className="icon-group-cell px-4 py-2"></td>
                        <td className="cell-stake positive px-4 py-2 text-center text-black font-bold">
                          {market.stake.toFixed(2)}
                        </td>
                        <td className="cell-selection-2 px-4 py-2 text-center">
                          <span className={`font-bold ${market.pl >= 0 ? 'positive text-green-500' : 'negative text-red-500'}`}>
                            {market.pl.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))
            )}
          </div>
        </div>
      </div>
 
  );
}
